import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import BottomDrawer from '../../Drawer/Drawer';
import SfhPrimaryButton from './SfhPrimaryButton';
import SfhSecondaryButton from './SfhSecondaryButton';
import { ReactComponent as RadioOn } from '../../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../../assets/images/svg/radio-off.svg';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';

const OTHER = 'other';
const REASONS = ['changedMind', 'foundBetterPrice', 'orderedByMistake', OTHER];

const HeaderWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding-top: 8px;
`;

const AttentionIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${themeColors.colorBackgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 32px;
    height: 32px;
  }
`;

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const Subtitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  color: ${themeColors.colorTextSecondary};
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 4px;
  padding: 12px 16px;
  margin-top: 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  > span {
    color: ${themeColors.colorTextSecondary};
    font-weight: 500;
  }
`;

const OptionsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: -12px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorTextSecondary : 'transparent'};
  border-radius: 4px;
  padding: 12px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  color: ${themeColors.colorTextPrimary};

  &:focus {
    outline: none;
  }
`;

const RadioIndicator = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  > svg {
    width: 20px;
    height: 20px;
  }
`;

const ReasonInput = styled.input`
  grid-column: 1 / -1;
  width: 100%;
  border: none;
  border-radius: 4px;
  padding: 12px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  color: ${themeColors.colorTextPrimary};
  background: ${themeColors.colorBackgroundSecondary};
  box-sizing: border-box;

  &::placeholder {
    color: ${themeColors.colorTextSecondary};
  }

  &:focus {
    outline: none;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: 12px;

  > button {
    flex: 1;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;

    /* In a column flex, "flex: 1" makes flex-basis (0%) drive the main-axis
       size (height) and override the buttons' 44px. Reset to flex: none so
       the fixed 44px height holds. */
    > button {
      flex: none;
    }
  }
`;

const ErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorError};
  text-align: center;
  margin-bottom: 8px;
`;

const SellFromHomeCancelOrderDrawer = ({
  isOpen,
  onClose,
  onConfirm,
  subtitle,
  isConfirming,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherText, setOtherText] = useState('');
  const inputRef = useRef(null);

  const isOther = selectedReason === OTHER;
  // Disabled until a reason is picked; if "Other", until the free-text is typed.
  // Also locked while the cancel request is in flight.
  const confirmDisabled =
    !selectedReason || (isOther && !otherText.trim()) || isConfirming;

  // When "Other" is picked, focus the free-text field and scroll it into view.
  useEffect(() => {
    if (isOther && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isOther]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setOtherText('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (confirmDisabled) return;
    // Send the human-readable reason as CancellationReason: the selected
    // option's label for presets, or the trimmed free text for "Other".
    onConfirm(
      isOther
        ? otherText.trim()
        : t(`sellWizard.sfhWizard.cancelDrawer.reason_${selectedReason}`),
    );
  };

  const footer = (
    <>
      {errorMessage && (
        <ErrorText data-cy="text-sfh-cancel-error">{errorMessage}</ErrorText>
      )}
      <FooterRow>
        <SfhSecondaryButton
          disabled={confirmDisabled}
          onClick={handleConfirm}
          data-cy="button-sfh-confirm-cancel"
        >
          {t('sellWizard.sfhWizard.cancelDrawer.confirm')}
        </SfhSecondaryButton>
        <SfhPrimaryButton
          disabled={isConfirming}
          onClick={onClose}
          data-cy="button-sfh-dismiss-cancel"
        >
          {t('sellWizard.sfhWizard.cancelDrawer.dismiss')}
        </SfhPrimaryButton>
      </FooterRow>
    </>
  );

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
      maxHeight="85%"
    >
      <HeaderWrap>
        <AttentionIcon>
          <AmberAlertTriangle />
        </AttentionIcon>
        <Title>{t('sellWizard.sfhWizard.cancelDrawer.title')}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </HeaderWrap>

      <NoticeBox>
        <AmberAlertTriangle />
        <span>
          {t('sellWizard.sfhWizard.transactionHistory.marketLossNotice')}
        </span>
      </NoticeBox>

      <OptionsList data-cy="list-sfh-cancel-reasons">
        {REASONS.map((reason) => {
          const selected = selectedReason === reason;
          return (
            <OptionCard
              key={reason}
              type="button"
              $selected={selected}
              onClick={() => setSelectedReason(reason)}
              data-cy={`radio-sfh-reason-${reason}`}
            >
              <RadioIndicator>
                {selected ? <RadioOn /> : <RadioOff />}
              </RadioIndicator>
              <span>
                {t(`sellWizard.sfhWizard.cancelDrawer.reason_${reason}`)}
              </span>
            </OptionCard>
          );
        })}
        {isOther && (
          <ReasonInput
            ref={inputRef}
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={t(
              'sellWizard.sfhWizard.cancelDrawer.otherPlaceholder',
            )}
            data-cy="input-sfh-cancel-reason"
          />
        )}
      </OptionsList>
    </BottomDrawer>
  );
};

SellFromHomeCancelOrderDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  subtitle: PropTypes.string,
  isConfirming: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default SellFromHomeCancelOrderDrawer;
